// Canonical emotion vocabulary for the Echo capture picker (v2 — 23 codes).
//
// Layout: high-arousal joyful → heart-warm → calm-positive → negatives
// (alphabetical) → neutral escape hatch. Row structure matches the reference
// image so wrap points land intentionally.
//
// Each entry carries:
//   label     — human copy shown on the chip and in tooltips
//   score     — 1..10 dashboard score (finer-grained than the legacy 5-bucket
//               scale so the Emotional Resonance chart reflects the picked
//               emotion, not just its valence bucket)
//   bg / fg   — pastel chip bg + darker text; each pair passes WCAG AA on the
//               warm stone-50 backdrop and works in light + dark mode
//   moodCode  — legacy 5-code bucket (rough/low/neutral/good/great). The
//               backend keeps writing mood_before/mood_after from this so
//               rolling back the FE picker restores the old 5-emoji scale
//               with no data migration.

export const EMOTIONS = [
  'joy', 'excited', 'happy',
  'grateful', 'glad', 'content',
  'peaceful', 'awe', 'relief',
  'anger', 'annoyed', 'anxious', 'ashamed',
  'disappointed', 'frustrated', 'guilty', 'hurt',
  'lonely', 'overwhelmed', 'sad', 'tired',
  'numb', 'mixed',
];

export const EMOTION_META = {
  joy:          { label: 'Joy',          score: 10,  bg: '#F4C97E', fg: '#7A5A0F', moodCode: 'great' },
  excited:      { label: 'Excited',      score: 10,  bg: '#F5B76B', fg: '#7C4E14', moodCode: 'great' },
  happy:        { label: 'Happy',        score: 9.5, bg: '#F5DE93', fg: '#6E5A1F', moodCode: 'great' },
  grateful:     { label: 'Grateful',     score: 9,   bg: '#D9E7B8', fg: '#4E6329', moodCode: 'great' },
  glad:         { label: 'Glad',         score: 8.5, bg: '#CDE4B7', fg: '#3F5F21', moodCode: 'good' },
  content:      { label: 'Content',      score: 8,   bg: '#BEE0C4', fg: '#2E5C3E', moodCode: 'good' },
  peaceful:     { label: 'Peaceful',     score: 8,   bg: '#B7E3D6', fg: '#23594B', moodCode: 'good' },
  awe:          { label: 'Awe',          score: 7.5, bg: '#B8DFE0', fg: '#235B62', moodCode: 'good' },
  relief:       { label: 'Relief',       score: 7,   bg: '#C5DDEF', fg: '#295269', moodCode: 'good' },
  numb:         { label: 'Numb',         score: 5.5, bg: '#D4D0CC', fg: '#4B4642', moodCode: 'neutral' },
  mixed:        { label: 'Mixed',        score: 5.5, bg: '#DCD5CD', fg: '#524A3F', moodCode: 'neutral' },
  tired:        { label: 'Tired',        score: 4.5, bg: '#D0CFDA', fg: '#40405E', moodCode: 'low' },
  annoyed:      { label: 'Annoyed',      score: 4,   bg: '#F1CFCB', fg: '#843A2D', moodCode: 'low' },
  frustrated:   { label: 'Frustrated',   score: 4,   bg: '#EFCFA8', fg: '#6E4713', moodCode: 'low' },
  disappointed: { label: 'Disappointed', score: 4,   bg: '#CDD0EB', fg: '#363F7A', moodCode: 'low' },
  guilty:       { label: 'Guilty',       score: 3.5, bg: '#CFC0DE', fg: '#4A2E62', moodCode: 'low' },
  lonely:       { label: 'Lonely',       score: 3.5, bg: '#C7CFE4', fg: '#2F3D69', moodCode: 'low' },
  sad:          { label: 'Sad',          score: 3,   bg: '#C7D9EE', fg: '#2A466B', moodCode: 'low' },
  anxious:      { label: 'Anxious',      score: 3,   bg: '#E4D3EF', fg: '#4E3672', moodCode: 'low' },
  anger:        { label: 'Anger',        score: 2,   bg: '#EFC7C4', fg: '#7B2F2A', moodCode: 'rough' },
  ashamed:      { label: 'Ashamed',      score: 2,   bg: '#D9C6E2', fg: '#4A2E62', moodCode: 'rough' },
  hurt:         { label: 'Hurt',         score: 2,   bg: '#CBD5E6', fg: '#2F3F63', moodCode: 'rough' },
  overwhelmed:  { label: 'Overwhelmed',  score: 1.5, bg: '#C6CFDE', fg: '#2E3B5A', moodCode: 'rough' },
};

// Groups the flat EMOTIONS list into visual rows for the picker. The picker
// consumes this so a hard-coded row structure isn't duplicated in JSX.
export const EMOTION_ROWS = [
  ['joy', 'excited', 'happy'],
  ['grateful', 'glad', 'content'],
  ['peaceful', 'awe', 'relief'],
  ['anger', 'annoyed', 'anxious', 'ashamed'],
  ['disappointed', 'frustrated', 'guilty', 'hurt'],
  ['lonely', 'overwhelmed', 'sad', 'tired'],
  ['numb', 'mixed'],
];

// 'not_sure' — a valid answer, deliberately NOT one of the 23 emotions above.
//
// Since 2026-09-23 the picker is no longer a gate: a take saves whether or not
// it is labelled, so a user can now say "I don't know" instead of being unable
// to continue. Two cases that must stay distinguishable:
//
//   null         the picker was never opened — no answer
//   'not_sure'   the picker was opened and declined — an answer of "unknown"
//
// It is kept out of EMOTIONS / EMOTION_META on purpose. Every consumer of those
// reads a `score`, and the only honest score for "I don't know" is none at all:
// giving it 5.5 would draw a point in the middle of the resonance chart that the
// user never reported. isEmotion() therefore stays false for it, which is what
// keeps resolveScore() and the dashboard aggregates correct by construction.
// Use isEmotionInput() for "may this be stored / sent", and hasLabel() in
// moodScale for "is there a real affect value here".
export const NOT_SURE = 'not_sure';

// Muted stone chip so it reads as an absence of an answer rather than a 24th
// feeling. Passes AA on both the warm stone-50 page and the dark card.
export const NOT_SURE_META = { label: 'Not sure', bg: '#E4E1DE', fg: '#57534E' };

const CODE_SET = new Set(EMOTIONS);

/** One of the 23 SCORED emotions. False for 'not_sure' — by design. */
export const isEmotion = (code) =>
  typeof code === 'string' && CODE_SET.has(code);

export const isNotSure = (code) => code === NOT_SURE;

/** Anything the picker may produce and the backend will store. */
export const isEmotionInput = (code) => isEmotion(code) || isNotSure(code);

export const emotionToLabel = (code) => {
  if (isEmotion(code)) return EMOTION_META[code].label;
  if (isNotSure(code)) return NOT_SURE_META.label;
  return null;
};

export const emotionToScore = (code) =>
  (isEmotion(code) ? EMOTION_META[code].score : null);

// No valence bucket for 'not_sure': the legacy 5-code scale has no cell for
// "unknown", and 'neutral' is a claim about how someone felt.
export const emotionToMoodCode = (code) =>
  (isEmotion(code) ? EMOTION_META[code].moodCode : null);

export const emotionToBg = (code) => {
  if (isEmotion(code)) return EMOTION_META[code].bg;
  if (isNotSure(code)) return NOT_SURE_META.bg;
  return null;
};

export const emotionToFg = (code) => {
  if (isEmotion(code)) return EMOTION_META[code].fg;
  if (isNotSure(code)) return NOT_SURE_META.fg;
  return null;
};
